export const inr = (n) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 }).format(Number(n) || 0);

export const num = (n) => Number(n || 0);

// dd-mm-yyyy
export function fmtDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}-${mm}-${d.getFullYear()}`;
}

// value -> Date (dd-mm-yyyy) for date inputs
export function toDateInput(value) {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

// 'yyyy-mm-dd' (date input) -> 'dd-mm-yyyy'
export function toDisplayDate(input) {
  if (!input) return '';
  const m = String(input).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  return input;
}

export function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + Number(days || 0));
  return d;
}

export function todayISO() {
  const d = new Date();
  return toDateInput(d);
}

// Resolve a backend-relative asset path (e.g. /uploads/abc.png) to a full URL
// using the API base from env, so images/audio never hit the frontend origin.
export function assetUrl(p) {
  if (!p) return '';
  if (/^(https?:|data:|\/\/)/.test(p)) return p; // already absolute / protocol-relative / data URI
  const base = (import.meta.env.VITE_API_URL || '').replace(/\/api\/?$/, '').replace(/\/+$/, '');
  return base ? `${base}${p.startsWith('/') ? '' : '/'}${p}` : p;
}

export function initials(name = '') {
  return name
    .split(' ')
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

export function downloadCSV(filename, headers, rows) {
  const esc = (v) => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [headers.map(esc).join(',')];
  for (const r of rows) lines.push(headers.map((h) => esc(r[h])).join(','));
  const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
// A date written as dd-mm-yyyy. Excel reads such a value as a date, reformats
// it to its own locale and shows ##### whenever the column is narrow, which
// reads as an empty cell. Sent as text, it stays exactly as the report showed
// it.
const DMY = /^\d{2}-\d{2}-\d{4}$/;

/**
 * Write the same rows as an Excel sheet. Numbers stay numbers so they can be
 * summed; dates go over as text so they arrive readable.
 */
export function downloadExcel(filename, headers, rows) {
  const esc = (v) => String(v == null ? '' : v)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const cell = (v) => {
    const text = esc(v);
    // \@ is Excel's "treat this as text" number format.
    return DMY.test(String(v ?? ''))
      ? `<td style="mso-number-format:'\\@'">${text}</td>`
      : `<td>${text}</td>`;
  };
  const html = `<table border="1"><thead><tr>${
    headers.map((h) => `<th>${esc(h)}</th>`).join('')
  }</tr></thead><tbody>${
    rows.map((r) => `<tr>${headers.map((h) => cell(r[h])).join('')}</tr>`).join('')
  }</tbody></table>`;
  const blob = new Blob(
    [`\uFEFF<html xmlns:x="urn:schemas-microsoft-com:office:excel"><head><meta charset="utf-8"></head><body>${html}</body></html>`],
    { type: 'application/vnd.ms-excel;charset=utf-8;' },
  );
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
